import { z } from 'zod';

const DEFAULT_ERROR_MAP: z.ZodErrorMap = (issue, ctx) => {
  if (issue.code === 'invalid_type') {
    return { message: 'Invalid number' };
  }
  return { message: ctx.defaultError };
};

type Validator = { value: number; message: string };

/**
 * {@link makeNumberSchema} constructs a schema that ensures the value is a number between a given range,
 * and accepts null as a valid value.  Inputs should use a `setValueAs` to ensure only null or a number is
 * provided to the schema.
 */
export function makeNumberSchema(
  { min, max }: { min: Validator; max: Validator },
  params: Parameters<typeof z.number>[0] = { errorMap: DEFAULT_ERROR_MAP }
) {
  return z.union([z.number(params).int().min(min.value, min.message).max(max.value, max.message), z.null()]);
}

/**
 * {@link makeNonNullNumberSchema} constructs a schema that ensures the value is a number between a given range.
 */
export function makeNonNullNumberSchema(
  { min, max }: { min: Validator; max: Validator },
  params: Parameters<typeof z.number>[0] = { errorMap: DEFAULT_ERROR_MAP }
) {
  return z.number(params).int().min(min.value, min.message).max(max.value, max.message);
}

/**
 * {@link makeStrictNumberSchema} constructs a schema that ensures the value is a number between a given range.
 */
export function makeStrictNumberSchema({
  min,
  max,
  required,
}: {
  min: Validator;
  max: Validator;
  required: { message: string };
}) {
  return makeNonNullNumberSchema(
    { min, max },
    {
      errorMap: (issue, ctx) => {
        if (issue.code === 'invalid_type') {
          if (issue.received === 'null') {
            return { message: required.message };
          }
          return { message: 'Invalid number' };
        }
        return { message: ctx.defaultError };
      },
    }
  );
}
